import * as React from 'react'
import {
  useInvokeApi,
  usePageNotifications,
  useAuth,
  useDiagnosticsApi,
} from '../../hooks'
import { Formik } from 'formik'
import { FormikTextField, EditModalActionBar } from '../../components'
import { Form, Box, Loader, Checkbox, Grid } from 'cs-kevlar-react'
import {
  LocationSharingConfigurationViewModel,
  SupportUserPermission,
} from '../../api'
import {
  makeStyles,
  useTheme,
  Theme,
  createStyles,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { useContext } from 'react'
import { AsyncPayload } from '../base'
import { number } from 'prop-types'

interface EditLocationSharingConfigurationProps {
  onClose?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
  }),
)

interface LocationSharingConfigurationForm {
  updateInterval: number
  forceOverrideAllDeviceConfigurations: boolean
}

export const EditLocationSharingConfiguration: React.FunctionComponent<EditLocationSharingConfigurationProps> = ({
  onClose,
}) => {
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const { dispatch: dispatchNotification } = usePageNotifications()

  const {
    getLocationSharingConfiguration,
    updateLocationSharingConfiguration,
  } = useDiagnosticsApi()

  const [
    currentLocationSharingInterval,
    setLocationSharingConfiguration,
  ] = React.useState<AsyncPayload<LocationSharingConfigurationViewModel>>({
    payload: null,
    isLoading: true,
  })

  if (authUser === null) return null

  const onEdit = (values: LocationSharingConfigurationForm, actions: any) => {
    invokeApi(
      () =>
        updateLocationSharingConfiguration({
          forceOverrideAllDeviceConfigurations:
            values.forceOverrideAllDeviceConfigurations,
          deviceLocationSharingIntervalInSeconds: values.updateInterval,
        }),
      'There was an error updating the location sharing configuration.',
      undefined,
      () => {
        dispatchNotification({
          type: 'SetSuccess',
          payload: `Location sharing configuration.`,
        })

        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  React.useEffect(() => {
    const loadLocationSharingConfigurationData = async () => {
      const response = await getLocationSharingConfiguration()
      if (response.status === 200 && response.data) {
        setLocationSharingConfiguration({
          payload: response.data.data,
          isLoading: false,
        })
      }
    }
    loadLocationSharingConfigurationData()
  }, [])

  const canEdit =
    authUser.isSupport &&
    authUser.permissions.includes(
      SupportUserPermission.ManageDiagnosticConfiguration,
    )

  const isLoading = currentLocationSharingInterval.isLoading
  const initialValues = React.useMemo(() => {
    return {
      updateInterval:
        currentLocationSharingInterval?.payload
          ?.deviceLocationSharingIntervalInSeconds ?? 0,
      forceOverrideAllDeviceConfigurations:
        currentLocationSharingInterval?.payload
          ?.forceOverrideAllDeviceConfigurations ?? false,
    }
  }, [currentLocationSharingInterval.payload])

  return (
    <React.Fragment>
      {currentLocationSharingInterval.payload !== null && (
        <Formik
          initialValues={initialValues}
          validationSchema={null}
          onSubmit={onEdit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid container direction="column">
                  <Grid item>
                    <Checkbox
                      id="forceOverrideAllDeviceConfigurations"
                      label="Force Override All Device Location Sharing Configurations"
                      checked={
                        formikProps.values.forceOverrideAllDeviceConfigurations
                      }
                      onChange={formikProps.handleChange}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName="updateInterval"
                      label="Update Interval"
                      type="number"
                      fullWidth
                      disabled={
                        !formikProps.values.forceOverrideAllDeviceConfigurations
                      }
                      required
                    />
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              {
                <EditModalActionBar
                  canDelete={false}
                  onCancel={onClose}
                  formikProps={formikProps}
                  isLoading={isLoading}
                  canSubmit={canEdit}
                  loader={
                    <Loader size="sm" text="Saving" disableVerticalGutters />
                  }
                />
              }
            </Form>
          )}
        </Formik>
      )}
      {currentLocationSharingInterval.payload === null && (
        <Loader size="sm" text="Loading location sharing configuration" />
      )}
    </React.Fragment>
  )
}
